import { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import { useWebSocket } from '../hooks/useWebSocket';
import { Bell, WifiOff } from 'lucide-react';
import { LunchBreakButton } from './LunchBreakButton';
import { NetworkDowntimeButton } from './NetworkDowntimeButton';
import { PunchButton } from './PunchButton';
import { EndMyDayButton } from './EndMyDayButton';
import MasterDataGate from './MasterDataGate';
import { useHeartbeat } from '../hooks/useHeartbeat';

function OfflineBanner() {
  const [offline, setOffline] = useState(typeof navigator !== 'undefined' && navigator.onLine === false);
  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  if (!offline) return null;
  return (
    <div className="bg-amber-500 text-white px-4 py-1.5 flex items-center justify-center gap-2 text-xs font-medium" data-testid="offline-banner">
      <WifiOff className="h-3.5 w-3.5" />
      You are offline — changes will sync when you reconnect.
    </div>
  );
}

function NotificationBanner() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      setShow(true);
    }
  }, []);

  if (!show) return null;

  const handleEnable = () => {
    Notification.requestPermission().then((perm) => {
      setShow(false);
    });
  };

  return (
    <div className="bg-[#4338CA] text-white px-4 py-2 flex items-center justify-between text-sm" data-testid="notification-permission-banner">
      <div className="flex items-center gap-2">
        <Bell className="h-4 w-4" />
        <span>Enable browser notifications to get alerts even when this tab is in the background.</span>
      </div>
      <div className="flex items-center gap-2">
        <button onClick={handleEnable} className="bg-white text-[#4338CA] px-3 py-1 rounded-md text-xs font-semibold hover:bg-slate-100" data-testid="enable-notifications-btn">
          Enable
        </button>
        <button onClick={() => setShow(false)} className="text-white/70 hover:text-white text-xs" data-testid="dismiss-notifications-btn">
          Dismiss
        </button>
      </div>
    </div>
  );
}

export default function Layout({ children }) {
  useWebSocket();
  useHeartbeat();
  return (
    <div className="flex h-screen overflow-hidden">
      <Sidebar />
      <main
        className="flex-1 h-screen overflow-y-auto overflow-x-hidden ml-0 md:ml-64 pt-14 md:pt-0"
        style={{ scrollbarGutter: 'stable' }}
        data-testid="main-content"
      >
        <OfflineBanner />
        <NotificationBanner />
        {/* Sticky top-right action strip — Punch / Network / Lunch / End my day.
            Sits above page content so it never collides with the page header's
            own buttons (e.g. "Create Task"). Hidden on mobile (< md) where the
            mobile header bar already serves the role. */}
        <div
          className="sticky top-0 z-40 hidden md:flex justify-end gap-2 px-4 py-2 bg-white/95 backdrop-blur border-b border-slate-200"
          data-testid="floating-quick-actions"
        >
          <PunchButton />
          <NetworkDowntimeButton />
          <LunchBreakButton />
          <EndMyDayButton />
        </div>
        <MasterDataGate />
        {children}
      </main>
    </div>
  );
}
